import * as React from 'react'
import algoliaSearch from 'algoliasearch/lite'
import { Configure, Hits, Index, InstantSearch } from 'react-instantsearch-dom'
import AutoComplete from './AutoComplete'
import { colors } from '../shared/colors'
import styled from 'styled-components'
import { GoodSansB2 } from '../shared/typography'
import SearchIcon from '@components/shared/icons/SearchIcon'
import XIcon from '@components/shared/icons/XIcon'
import { Col, config, Row } from 'react-awesome-styled-grid'
import { useRouter } from 'next/router'
import type { SearchPagePropsType, SearchSectionType } from '../../models/Search'
import { TagCardRow } from '@components/Tag/TagCardRow'
import { TrackListItem } from '@components/Track/TrackListItem'
import { getLibrary } from '@services/dipseaApi'
import { getTagSlug } from '../../models/Tag'
import { getSeriesSlugAsync } from '../../models/Series'
import { getCategorySlugAsync } from '../../models/Category'

const algoliaOpenSearchApiKey = '4d0036fe026c6c2e51fc130a40a68322'
const algoliaAppId = 'P74RHY3F8N'

const searchClient = algoliaSearch(algoliaAppId, algoliaOpenSearchApiKey)

function Hit (props: { hit: any }): React.ReactNode {
  return <TrackListItem id={props.hit.objectID} type={'id'} />
}

export type SearchStateType = {
  query?: string,
  createUrl?: () => void,
  currentRefinement?: string,
  hits?: { index: 'tags' | 'tracks' | 'categories' | 'series', hits: any[] }[]
}

export default function SearchPage (props: SearchPagePropsType): React.ReactNode {
  React.useEffect((): void => {
    // prefetch library/store in global context
    getLibrary()
  })
  const router = useRouter()
  const setTag = (id: string): void => {
    getTagSlug(id).then((slug: string): void => {
      router.push(slug)
    })
  }
  const setCategory = (id: string): void => {
    getCategorySlugAsync(id).then((slug: string | null): void => {
      if (slug) {
        router.push(slug)
      }
    })
  }
  const setSeries = (id: string): void => {
    getSeriesSlugAsync(id).then((slug: string): void => {
      router.push(slug)
    })
  }

  const clearSearchState = (): void => {
    setSearchState(null)
    setSearchActive(false)
  }

  const updateSearchState = (searchState: SearchStateType): void => {
    setSearchState(searchState)
    setSearchActive(!!searchState.query)
  }
  const [searchState, setSearchState] = React.useState<SearchStateType | null>(null)
  const [searchActive, setSearchActive] = React.useState<boolean>(false)
  return (
    <Row>
      <Col>
        <Title>Search</Title>
        <SearchContainer>
          <SearchIconContainer>
            <SearchIcon size={20} color={colors.white25}/>
          </SearchIconContainer>
          {searchActive && (
            <XButton
              onClick={clearSearchState}
              onKeyDown={(event: any): void => {
                if (event.keyCode === 13) {
                  clearSearchState()
                }
              }}
              role="button"
              aria-label="Clear search"
              tabIndex={0}
            >
              <XIcon
                isCircleStyle
                size={20}
                color={colors.white25}
              />
            </XButton>
          )}
          <InstantSearch
            indexName="tracks"
            searchClient={searchClient}
            onSearchStateChange={updateSearchState}
            searchState={searchState || {}}
          >
            <AutoComplete
              searchActive={searchActive}
              selectTag={(id: string): void => setTag(id)}
              selectCategory={(id: string): void => setCategory(id)}
              selectSeries={(id: string): void => setSeries(id)}
            />
            <Index indexName="tracks">{searchActive && <Hits hitComponent={Hit}/>}</Index>
            <Index indexName="tags"/>
            <Index indexName="categories"/>
            <Index indexName="series"/>
            <Configure hitsPerPage={40}/>
          </InstantSearch>
        </SearchContainer>
        {!searchActive && props.sections.map((rec: SearchSectionType, index: number): React.ReactNode => (
          <RowContainer key={`search-section-${index}`}>
              <SectionTitle>{rec.title}</SectionTitle>
              <TagCardRow tags={rec.items}/>
          </RowContainer>
        ))}
      </Col>
    </Row>
  )
}

const Title = styled.h1<any>`
    margin: 32px 0 19px 0;
    color: ${colors.white};
    font-family: "Queens Condensed Light";
    font-size: 36px;
    font-style: normal;
    font-weight: 400;
    line-height: 100%;
    ${(props: any): any => config(props).media.xs`
  `}
    ${(props: any): any => config(props).media.sm`
      font-size: 45px;
      margin: 32px 0 24px 0;
    `}
    ${(props: any): any => config(props).media.md`
      font-size: 60px;
      margin: 60px 0 28px 0;
  `}
    ${(props: any): any => config(props).media.lg`
  `}
    ${(props: any): any => config(props).media.xl`
  `}
`

const SectionTitle = styled.h4<any>`
    font-family: "Good Sans Regular";
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 125%; /* 125% */
    letter-spacing: -0.3px;
    color: ${colors.white};
    padding: 0;
    margin: 0 0 16px;

    ${(props: any): any => config(props).media.lg`
      font-size: 24px;
      margin: 0 0 20px;
  `}
`

const RowContainer = styled.div<any>`
    margin: 24px 0px;
    display: flex;
    flex-direction: column;

`

const SearchIconContainer = styled.div<any>`
    align-items: center;
    display: flex;
    height: 44px;
    justify-content: center;
    left: 0;
    margin: 0;
    position: absolute;
    top: 0;
    width: 44px;

    svg {
        padding: 0 0 2px 0;
    }
`
const XButton = styled.button<any>`
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    height: 44px;
    justify-content: center;
    margin: 0;
    outline: none;
    padding: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 44px;

    ${(props: any): any => props.showHover && (
            `:hover {
      opacity: 0.5;
    }`
    )}

`
const SearchContainer = styled.div<any>`
    position: relative;

    input {
        ${GoodSansB2};
        background-color: ${colors.midnightBlueTint};
        border: 0;
        border-radius: 4px;
        height: 44px;
        line-height: 44px;
        outline: none;
        padding: 0 40px;
        /* width - padding horizontal */
        width: calc(100% - 80px);
        ${(props: any): any => config(props).media.lg`
      font-size: 16px;
    `};

        ::placeholder {
            color: ${colors.white};
            opacity: .5;
        }

        :-ms-input-placeholder {
            color: ${colors.white};
            opacity: .5;
        }

        ::-ms-input-placeholder {
            color: ${colors.white};
            opacity: .5;
        }
    }

    ul {
        margin: 0;
        padding: 0;

        li {
            list-style: none;
            margin: 0;
            padding: 16px 0;
            border-bottom: 0.5px solid ${colors.white50};
        }
    }

    div.ais-Hits {
        
    }

    .react-autosuggest__suggestions-container--open {
        background-color: ${colors.midnightBlueTint};
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
        left: 0;
        margin: 10px 0 0 0;
        max-height: 33vh;
        overflow: scroll;
        overflow-x: hidden;
        position: relative;
        right: 0;
        top: 0;
        z-index: 10;
        scrollbar-color: ${colors.white25} ${colors.midnightBlueTint};

        ::-webkit-scrollbar {
            display: block;
            width: 18px;
        }

        ::-webkit-scrollbar-thumb {
            border: 6px solid rgba(0, 0, 0, 0);
            background-clip: padding-box;
            border-radius: 10px;
            background-color: ${colors.white25};
        }

        ::-webkit-scrollbar-button {
            width: 0;
            height: 0;
            display: none;
        }

        ::-webkit-scrollbar-corner {
            background-color: transparent;
        }
    }
`
